import React, { useContext, useEffect, useMemo, useState } from 'react'
import './index.scss'
import { ICONS } from '@Const/common'
import { StoresContext } from '@App/contexts'
import Button from '@Components/Buttons/Button'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import { renderOverclaimEmailTemplate } from '@App/api/routes/overclaim'
import Popup from '@Components/Popups/Popup'
import ConfirmationPopup from '@Components/Popups/ConfirmationPopup'
import { observer } from 'mobx-react'

export interface DisputeEmailFormProps {
    onClose: () => void
}

const DisputeEmailForm = (props: DisputeEmailFormProps) => {
    const { onClose } = props
    const { userStore, rightsholderStore, overclaimViewStore } = useContext(StoresContext)
    const [emailTemplate, setEmailTemplate] = useState<string>()
    const [emailNote, setEmailNote] = useState<string>('')
    const [emailSubmitted, setEmailSubmitted] = useState(false)
    const overclaimViewItem = overclaimViewStore.overclaimViewItem
    const publisherNames = useMemo(
        () =>
            overclaimViewItem?.originalPublishers
                .filter(
                    (pub, index, publishers) =>
                        pub.topMemberIpId !== rightsholderStore.currentRightsholderId &&
                        publishers.findIndex((p) => p.topMemberIpId === pub.topMemberIpId) === index
                )
                .map((pub) => pub.topMemberName)
                .join(', '),
        [overclaimViewItem?.originalPublishers, rightsholderStore.currentRightsholderId]
    )

    useEffect(() => {
        renderOverclaimEmailTemplate(
            overclaimViewItem?.overclaimHeaderId ?? 0,
            rightsholderStore.currentRightsholderId
        ).then((result) => {
            setEmailTemplate(result.data.replaceAll('&#39;', "'").replaceAll(/\(http.*?\)/g, ''))
        })
    }, [])

    const onEmailNoteChange = (event: any) => setEmailNote(event.target.value)

    const onSend = async () => {
        await overclaimViewStore.sendOverclaimEmail(emailNote)
        setEmailSubmitted(true)
    }

    return (
        <>
            {emailSubmitted ? (
                <Popup className="dispute-success-popup" onClose={onClose} showClose>
                    <ConfirmationPopup title="Email Sent!" confirmButtonText="Done" onConfirm={onClose}>
                        <div className="dispute-success-text">
                            An email has been sent to all parties involved in this overclaim
                        </div>
                    </ConfirmationPopup>
                </Popup>
            ) : (
                <Popup className="dispute-popup" onClose={() => {}}>
                    <div className="dispute-email-form">
                        <div className="dispute-email-form-header">
                            <img src={ICONS.email} alt="email icon" />
                            <h5>Email All Claimants</h5>
                            <span>
                                This email will be sent to all claimants involved on this work to attempt to resolve the
                                overclaim.
                            </span>
                        </div>
                        <div className="email-content-section">
                            <div className="email-content-header">
                                <span className="to-cc-section">
                                    <div className="recipient-line">
                                        <strong>To:</strong>
                                        {publisherNames}
                                    </div>
                                    <div className="cc-line">
                                        <strong>Cc: </strong> {userStore.userInfo.email}
                                    </div>
                                </span>
                                <br />
                                <span>
                                    <>
                                        <strong>Subject: </strong>
                                        Overclaim on your work <strong>{overclaimViewItem?.title}</strong> (Overclaim
                                        ID#
                                        {overclaimViewItem?.overclaimHeaderId})
                                    </>
                                </span>
                            </div>
                            <br />
                            <div className="email-content-body">{emailTemplate ?? <PointPreloader />}</div>
                        </div>
                        <div className="dispute-email-form-input">
                            <label htmlFor="dispute-email-note-input">Add your own note</label>
                            <input
                                id="dispute-email-note-input"
                                type="text"
                                value={emailNote}
                                onChange={onEmailNoteChange}
                            />
                            <div className="form-button-row">
                                <Button mainColor type="secondary" onClick={onClose}>
                                    Cancel
                                </Button>
                                <Button
                                    mainColor
                                    type="primary"
                                    onClick={onSend}
                                    disabled={userStore.userInfo.isInternal}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </div>
                </Popup>
            )}
        </>
    )
}

export default observer(DisputeEmailForm)
